import { Country } from 'library/ts/model/enums/geo/Country';
import { Domain } from 'library/ts/model/enums/web/Domain';
import { PluginUtilities } from 'library/ts/plugins/shared/utilities/PluginUtilities';
import { LogService } from 'library/ts/utilities/logger/LogService';
import { tagEvents } from 'tag/ts/tag/events/TagEvent';
import { TagConstants } from 'tag/ts/tag/model/TagConstants';
export class AbstractTagClass {
    #name;
    #tag;
    #logger;
    #plugins;
    constructor(argument = { name: 'AbstractTagClass', tag: undefined }) {
        this.#name = argument?.name;
        this.#tag = argument?.tag;
        this.#logger = LogService.create({ prefixes: ['SmartTag:', 'Tag:', this.#tag.uuid, this.#name] });
        const pluginsPhaseDone = () => {
            if (this.utils.Type.isFunction(argument?.onPluginsPhaseDone)) {
                argument?.onPluginsPhaseDone(this.plugins);
            }
        };
        this.#initializePlugins().then((data) => {
            if (this.utils.Type.isFunction(argument?.resolve)) {
                argument?.resolve({ msg: `${this.name} class is ready`, success: true, result: data });
            }
            pluginsPhaseDone();
        }).catch((data) => {
            if (this.utils.Type.isFunction(argument?.reject)) {
                argument?.reject({ msg: `${this.name} class has failed`, success: false, result: data });
            }
            pluginsPhaseDone();
        });
    }
    #initializePlugins = () => {
        return new Promise((resolve, reject) => {
            this.utils.Delay.delay().then(() => {
                PluginUtilities.loadPluginScripts({
                    uuid: this.uuid,
                    config: this?.tag?.model?.config?.tag,
                    ownerClassName: this.name
                }).then((data) => {
                    if (!data?.msg.toString().includes('No plugins marked for pre-load were found')) {
                        this?.logger?.log('initializePlugins:', 'loadPluginScripts:', data.msg, data);
                    }
                    this.setupPlugins({
                        ownerClass: this,
                        framework: {
                            model: this.tag.model,
                            view: this.tag.view,
                            controller: this.tag.controller
                        },
                        config: this.tag.model.config.tag
                    }).then((data) => {
                        if (!data?.msg.toString().includes('No plugins configs were found')) {
                            this?.logger?.log('initializePlugins:', data.msg, data);
                        }
                        resolve({ msg: `${this.name} plugins were initialized.` });
                    }).catch((data) => {
                        reject({ msg: `${this.name} plugins initialization has failed.`, result: data });
                    });
                }).catch((data) => {
                    reject({ msg: `${this.name} plugins load has failed.`, result: data });
                });
            });
        });
    };
    get name() {
        return this.#name;
    }
    get tag() {
        return this.#tag;
    }
    get tagMVC() {
        return {
            model: this?.tag?.model,
            view: this?.tag?.view,
            controller: this?.tag?.controller
        };
    }
    get logger() {
        return this.#logger;
    }
    get uuid() {
        return this.#tag.uuid;
    }
    get params() {
        return this.#tag?.params;
    }
    set params(value) {
        this.#tag.params = value;
    }
    get callbacks() {
        return this.#tag?.callbacks;
    }
    set callbacks(value) {
        this.#tag.callbacks = value;
    }
    get apiMetadata() {
        return this?.tag?.model?.apiMetadata;
    }
    get country() {
        return (this?.params?.CONTENT?.country ||
            this?.runtimeVO?.top?.country ||
            Country.US);
    }
    get utils() {
        return window?.SmartTag?.utilities;
    }
    get capabilities() {
        return window?.SmartTag?.capabilities;
    }
    get runtimeVO() {
        return window?.SmartTag?.runtimeVO;
    }
    get TagConstants() {
        return TagConstants.getInstance();
    }
    get tagConfig() {
        return this?.tag?.model?.config?.tag;
    }
    get plugins() {
        return this.#plugins;
    }
    set plugins(value) {
        this.#plugins = value;
    }
    get setupPlugins() {
        return PluginUtilities.setupPluginsForOwnerClass;
    }
    initializePluginsAtRuntime = (argument) => {
        return new Promise((resolve, reject) => {
            PluginUtilities.loadPluginScripts(argument).then((data) => {
                this.setupPlugins({
                    ownerClass: this,
                    framework: this.tagMVC,
                    config: this.tagConfig
                }).then((plugins) => {
                    console.info(this.name, plugins);
                    resolve({ msg: `${this.name}: plugins instantiated:`, plugins: plugins, success: true });
                });
            });
        });
    };
    get TAG_API() {
        return this.tag?.controller?.tagAPI?.OUT;
    }
    get dispatchTagEvent() {
        return this.#tag.dispatchTagEvent;
    }
    get getTagEventVO() {
        return this.#tag.getTagEventVO;
    }
    get getTagEventDataVO() {
        return this.#tag.getTagEventDataVO;
    }
    tagEventsHandler = (callback, events, subscriber = this) => {
        this.#tag.tagEventsHandler(callback, events, subscriber);
    };
    removeTagEventsHandler = (callback, eventDispatcher = this?.tag?.eventDispatcher, events = tagEvents()) => {
        return new Promise((resolve, reject) => {
            if (this.utils.Type.isArray(events) === false
                &&
                    this.utils.Type.isString(events) === true) {
                events = [events];
            }
            if (this.utils.Type.isDefined(callback)
                &&
                    this.utils.Type.isFunction(callback)) {
                for (let i = 0; i < events.length; i++) {
                    eventDispatcher.removeEventListener(events[i], callback);
                }
            }
            resolve({ msg: 'Event listeners removed' });
        });
    };
    get localStorageName() {
        return `'smart-tag-${this?.params?.CONTENT?.partner}`;
    }
    get settings() {
        return this?.utils?.Store?.get?.(this?.localStorageName);
    }
    isDesktop() {
        return !!window?.SmartTag?.capabilities?.isDesktop;
    }
    isPPlus() {
        return !!this?.runtimeVO?.top?.parsed?.host?.includes?.(Domain.PPLUS);
    }
    isLive() {
        return !!this?.tag?.model?.contentUtilities?.isLive?.(this.params, this.apiMetadata);
    }
    getCountryCode() {
        return this?.tag?.country?.toString();
    }
    isDAIAsset() {
        return this?.tag?.model?.contentUtilities?.isDAIAsset();
    }
    isDaiLiveStream() {
        return this?.tag?.model?.contentUtilities?.isLiveDAIStream(this?.tag?.model?.apiMetadata);
    }
    getMillstoneMetadata() {
        return this?.params?.CONTENT?.millstone;
    }
    getMillstoneDaiParams() {
        return this.getMillstoneMetadata()?.daiParams;
    }
    findMillstoneDaiParam(key) {
        return this.getMillstoneDaiParams()?.[key];
    }
}
